import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ErrorCodes, ProblemError, useApi } from "@csa/web-foundation";
import type { Page } from "@csa/web-foundation";
import type {
  ConceptRow,
  CoverageView,
  CreateCoverageInput,
  CreateConceptInput,
  CreatePackageInput,
  CreateProductInput,
  DistributablePackage,
  PackageVersionInput,
  PackageView,
  ProductVersionInput,
  ProductVersionRow,
  ProductView,
} from "../../api/catalog-contracts";

// Catalog step 2 self-service (product · package · coverage · concept). Same posture
// as the carrier management reads/writes (use-catalog-mgmt.ts): the governed, versioned
// catalog pattern (ADR 0004 §§4-5) — identity + append-only, whole-state versions. No
// business logic in the browser; the API is the boundary and re-enforces catalog:write
// @ all on every call. Equivalence writes are FOUR-EYES and are NOT wired here (the
// propose/confirm flow is flagged as a follow-on on the surface).

const CATALOG = ["catalog"];
const productKey = (code: string) => [...CATALOG, "products", code];
const packagesByLineKey = (line: string) => [...CATALOG, "packages", "line", line];
const packageKey = (code: string) => [...CATALOG, "packages", code];
const conceptsKey = (line: string) => [...CATALOG, "concepts", "line", line];
const coverageKey = (code: string) => [...CATALOG, "coverages", code];

/** A lookup read that treats the EXPECTED 404 ("no such code") as `null` — an empty
 *  result the surface renders as a gentle "not found" note, not a red error. */
async function lookup<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ProblemError && err.is(ErrorCodes.NOT_FOUND)) return null;
    throw err;
  }
}

// ── products ─────────────────────────────────────────────────────────────────

/** A9 — GET /v1/catalog/products: the product LIST (Page envelope, catalog:read @ all).
 *  Follows the cursor to the end for the same reason useCarriers does (use-catalog.ts):
 *  the curated catalog is a small bounded set and every consumer here is a picker, so a
 *  partial page would silently hide a product the operator can never select. Until this
 *  read existed the operator had to type the product code from memory. */
const PRODUCT_PAGE = 100;
const MAX_PRODUCT_PAGES = 20;

export function useProducts(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...CATALOG, "products", "list"],
    enabled,
    queryFn: async (): Promise<Page<ProductView>> => {
      const all: ProductView[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < MAX_PRODUCT_PAGES; page += 1) {
        const qs = new URLSearchParams({ limit: String(PRODUCT_PAGE) });
        if (cursor) qs.set("cursor", cursor);
        const res: Page<ProductView> = await api.get<Page<ProductView>>(
          `/v1/catalog/products?${qs.toString()}`,
        );
        all.push(...(res.data ?? []));
        cursor = res.nextCursor ?? null;
        if (!cursor) break;
      }
      return { data: all, nextCursor: null, hasMore: false };
    },
  });
}

/** A9 — GET /v1/catalog/products/:code/versions: the append-only version timeline. */
export function useProductVersions(code: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: code ? [...productKey(code), "versions"] : [...CATALOG, "products", "none", "v"],
    enabled: code !== null,
    queryFn: () =>
      lookup(() => api.get<Page<ProductVersionRow>>(`/v1/catalog/products/${code}/versions`)),
  });
}

/** GET /v1/catalog/products/:code — identity + current version. 404 → null. Kept
 *  alongside useProducts: the list carries `current`, but the detail panel reads
 *  through this key so a version-append invalidation lands in one place.
 *  catalog:read @ all. */
export function useProduct(code: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: code ? productKey(code) : [...CATALOG, "products", "none"],
    enabled: code !== null,
    queryFn: () => lookup(() => api.get<ProductView>(`/v1/catalog/products/${code}`)),
  });
}

/** POST /v1/catalog/products — create a product (identity + version 1). catalog:write
 *  @ all, audited. An already-existing code returns 409. */
export function useCreateProduct() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) => api.post<ProductView>("/v1/catalog/products", input),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: productKey(res.code) });
    },
  });
}

/** POST /v1/catalog/products/:code/versions — append a whole next-state (never a
 *  patch). catalog:write @ all, audited. On success refetch the product detail. */
export function useAddProductVersion(code: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductVersionInput) =>
      api.post<ProductView>(`/v1/catalog/products/${code}/versions`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: productKey(code) });
    },
  });
}

// ── packages ─────────────────────────────────────────────────────────────────

/** GET /v1/catalog/packages?line= — the distributable packages of a line, org-gated
 *  (the ONLY package LIST read; the same feed the advisor dropdown consumes).
 *  catalog:read @ all. */
export function usePackagesByLine(line: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: line ? packagesByLineKey(line) : [...CATALOG, "packages", "line", "none"],
    enabled: line !== null,
    queryFn: () =>
      api.get<Page<DistributablePackage>>(`/v1/catalog/packages?line=${encodeURIComponent(line!)}`),
  });
}

/** GET /v1/catalog/packages/:code — identity + current version + pinned coverage-set.
 *  404 → null. catalog:read @ all. */
export function usePackage(code: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: code ? packageKey(code) : [...CATALOG, "packages", "none"],
    enabled: code !== null,
    queryFn: () => lookup(() => api.get<PackageView>(`/v1/catalog/packages/${code}`)),
  });
}

/** POST /v1/catalog/packages — create a package (identity + version 1 + pinned
 *  coverage-set). catalog:write @ all, audited. 409 on a duplicate code. */
export function useCreatePackage() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePackageInput) => api.post<PackageView>("/v1/catalog/packages", input),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: [...CATALOG, "packages"] });
      void qc.invalidateQueries({ queryKey: packageKey(res.code) });
    },
  });
}

/** POST /v1/catalog/packages/:code/versions — append a version with a FRESH pinned
 *  coverage-set. catalog:write @ all, audited. On success refetch the detail. */
export function useAddPackageVersion(code: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PackageVersionInput) =>
      api.post<PackageView>(`/v1/catalog/packages/${code}/versions`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: packageKey(code) });
      void qc.invalidateQueries({ queryKey: [...CATALOG, "packages"] });
    },
  });
}

// ── concepts ─────────────────────────────────────────────────────────────────

/** GET /v1/catalog/lines/:line/concepts — the canonical concepts of a line.
 *  catalog:read @ all. */
export function useConcepts(line: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: line ? conceptsKey(line) : [...CATALOG, "concepts", "none"],
    enabled: line !== null,
    queryFn: () => api.get<Page<ConceptRow>>(`/v1/catalog/lines/${line}/concepts`),
  });
}

/** POST /v1/catalog/concepts — create a canonical concept (identity + version 1).
 *  catalog:write @ all, audited. 409 on a duplicate code. */
export function useCreateConcept() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateConceptInput) =>
      api.post<{ code: string }>("/v1/catalog/concepts", input),
    onSuccess: (_res, input) => {
      void qc.invalidateQueries({ queryKey: conceptsKey(input.lineCode) });
    },
  });
}

// ── coverages (equivalence MAPPING is four-eyes — flagged, not wired) ─────────

/** GET /v1/catalog/coverages/:code — identity + current version + sub-limits. 404 →
 *  null (there is NO coverage LIST endpoint). catalog:read @ all. */
export function useCoverage(code: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: code ? coverageKey(code) : [...CATALOG, "coverages", "none"],
    enabled: code !== null,
    queryFn: () => lookup(() => api.get<CoverageView>(`/v1/catalog/coverages/${code}`)),
  });
}

/** POST /v1/catalog/coverages — create a carrier coverage (identity + version 1).
 *  catalog:write @ all, audited (NOT four-eyes — only equivalence mapping is).
 *  409 on a duplicate code. */
export function useCreateCoverage() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCoverageInput) =>
      api.post<CoverageView>("/v1/catalog/coverages", input),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: coverageKey(res.code) });
    },
  });
}
